import React, {useContext} from 'react';
import {useFormContext} from 'react-hook-form';
import {Input as ReactstrapInput} from 'reactstrap';
import {FormFieldProps} from '@src/@types/components/Form';
import trans from '@src/helpers/trans';
import {InputType} from 'reactstrap/types/lib/Input';
import {FormFieldDesignContext} from '@src/contexts/FormFieldDesignContext';
import classnames from '@src/helpers/classnames';
import {IEmptyModel} from '@src/@interfaces/IEmptyModel';
import TriggerableField from '../utils/TriggerableField';

export type InputProps<Model extends IEmptyModel, T extends InputType = InputType> =
        { type: T, isInvalid?: boolean } &
        { triggerWrapperClassName?: string } &
        FormFieldProps<Model> &
        (T extends 'file' ? {
            multiple?: boolean,
            accept?: string,
            maxLength?: number,
        } : Record<string, never>) &
        (T extends 'textarea' ? {
            rows?: number,
            maxLength?: number,
        } : Record<string, never>) &
        (T extends 'number' ? {
            step?: number,
            min?: number,
            max?: number,
        } : Record<string, never>);

export default function Input<Model extends IEmptyModel>(props: InputProps<Model>) {
    const {register, getFieldState, formState} = useFormContext<Model>();
    const [design] = useContext(FormFieldDesignContext);

    const isInvalid = props.isInvalid ?? Boolean(getFieldState(props.fieldName as any, formState).error);

    const {ref, ...inputProps} = register(props.fieldName as any);

    return <TriggerableField
            trigger={props.config.trigger}
            componentToRender={ReactstrapInput}
            wrapperClassName={props.triggerWrapperClassName}
            innerRef={ref}
            {...inputProps}
            {...(props.config.attributes ?? {})}
            className={classnames({
                'is-invalid': isInvalid,
            }, props.config.cssClass, props.className)}
            invalid={isInvalid}
            bsSize={props.config.size || design.size}
            placeholder={
                props.config.placeholder ?
                        trans(props.config.placeholder) :
                        trans('Forms.enter_field', {field: props.label.toLowerCase()})}
            readOnly={props.config.readOnly === true || design.fieldsAreReadOnly || props.forceReadonly}
            disabled={props.config.disabled === true || design.fieldsAreDisabled || props.forceDisabled}
            type={props.type}
            id={props.id}
            multiple={props.multiple}
            value={props.forceValue}
            accept={props.accept}
            rows={props.rows}
            maxLength={props.maxLength}
            step={props.step}
            min={props.min}
            max={props.max}
    />;
}
